import {
  calcularConflitos,
  durationInDays,
  formatDayMonth,
  isObraCancelada,
  isPast,
  positionInterval,
  separarConflitos,
} from "@/modules/carga-trabalho";
import type {
  CargaTrabalhoAlocacao,
  CargaTrabalhoConflitoIgnorado,
  CargaTrabalhoIntervalo,
  CargaTrabalhoObra,
  CargaTrabalhoObrasData,
  GanttIndicators,
  IsoDate,
  Timeline,
} from "@/modules/carga-trabalho";
import type { ObraTipo } from "@/modules/recursos";
import {
  identificacaoDaObra,
  type IdentificacaoObra,
} from "@/shared/utils/formatar-identificacao-obra";
import { chaveMarcaMembro } from "@/modules/recursos/simulacao";
import {
  buildObraItem,
  conflictBands,
  diasText,
  NO_TEAM_LABEL,
  obraCardTitle,
  periodoText,
  recursoLabel,
  tipCardToText,
  toConflictGroup,
} from "./gantt-rows";
import type { GanttSegment, GanttTipCard } from "./gantt-rows";
import type { ConflictGroup } from "./gantt-local-rows";

/** Alocação reduzida ao que a rolagem da visão por Obras precisa (qualquer perspectiva a monta). */
export interface AlocacaoParaFoco {
  inicio: IsoDate;
  fim: IsoDate;
  obraCancelada: boolean;
}

/** Início da 1ª alocação visível que toca o período (cortado no início do período); sem nenhuma, o início do período. */
export function focoDaVisaoPorObras(
  alocacoes: AlocacaoParaFoco[],
  periodo: CargaTrabalhoIntervalo,
  canceladaVisivel: boolean,
): IsoDate {
  const inicios = alocacoes
    .filter((alocacao) => canceladaVisivel || !alocacao.obraCancelada)
    .filter((alocacao) => alocacao.fim >= periodo.inicio && alocacao.inicio <= periodo.fim)
    .map((alocacao) => (alocacao.inicio > periodo.inicio ? alocacao.inicio : periodo.inicio));
  return inicios.sort()[0] ?? periodo.inicio;
}

/** Linha fina da etapa Obra, com as datas de início e fim (Figma: visão por Obras). */
export interface ObraLine {
  left: number;
  width: number;
  start: string;
  end: string;
  tooltip: string;
  /** Card do tooltip; ausente, vale o texto de `tooltip`. */
  card?: GanttTipCard;
  past: boolean;
}

/** Pílula da alocação: planejamento, obra e fechamento, com a duração como rótulo. */
export interface ObraPill {
  left: number;
  width: number;
  segments: GanttSegment[];
  label: string;
  tooltip: string;
  /** Card do tooltip; ausente, vale o texto de `tooltip`. */
  card?: GanttTipCard;
  past: boolean;
  cancelada: boolean;
}

export interface ObraRowPerson {
  name: string;
  team: string | null;
  conflicts: number;
  conflictTooltip: string;
  /** Card do tooltip do selo de conflito (Figma 10357:86888). */
  conflictGroups?: ConflictGroup[];
}

/** Conteúdo de uma linha da visão por Obras: um profissional ou o resumo da obra. */
export interface ObraRowContent {
  /** Única por alocação: a key da linha. */
  key: string;
  /** Chave das marcas da simulação (`chaveMarcaMembro`): a alocação, ou obra e profissional. */
  marcaKey: string;
  person: ObraRowPerson | null;
  /** "N Profissionais" ou "Sem equipe definida", quando a linha é o resumo da obra. */
  summary: string | null;
  line: ObraLine | null;
  pill: ObraPill | null;
}

export interface ObraGroup {
  key: string;
  obraId: string;
  /** Período da etapa Obra (`YYYY-MM-DD`); `null` quando a obra não tem a etapa. */
  obraInicio: string | null;
  obraFim: string | null;
  /** Obra sem nenhum profissional alocado: habilita "Definir Equipe". */
  semEquipe: boolean;
  codigo: string;
  /** Tipos da obra, exibidos como tags coloridas abaixo do nome. */
  tipos: ObraTipo[];
  nome: string;
  embarcacao: string | null;
  /** `ID - nome da embarcação` e `ID - sigla` (ADR 0018): o rótulo da linha escolhe um pela largura. */
  rotulo: IdentificacaoObra;
  /** Sigla da embarcação: o alvo do Definir Equipe a leva, para o tooltip do calendário citar "ID - SIGLA". */
  embarcacaoSigla: string | null;
  /** Linha mostrada com a obra recolhida (ou sem equipe). */
  summary: ObraRowContent;
  /** Uma linha por profissional alocado. */
  members: ObraRowContent[];
}

/** Duração em meses: abreviada ("2m") nas pílulas curtas. */
function monthsLabel(intervalo: CargaTrabalhoIntervalo): string {
  const months = Math.max(1, Math.round(durationInDays(intervalo) / 30));
  return months <= 2 ? `${months}m` : `${months} meses`;
}

function buildLine(
  obra: CargaTrabalhoObra,
  timeline: Timeline,
  indicators: GanttIndicators,
  today: IsoDate,
): ObraLine | null {
  const etapa = obra.etapas.obra;
  if (!indicators.obra || !etapa || isObraCancelada(obra)) return null;
  const position = positionInterval(etapa, timeline);
  if (!position) return null;
  const card: GanttTipCard = {
    title: obraCardTitle(obra),
    subtitle: null,
    phases: [{ label: "Obra", tone: "obra", periodo: periodoText(etapa), dias: diasText(etapa) }],
  };
  return {
    left: position.left,
    width: position.width,
    start: formatDayMonth(etapa.inicio),
    end: formatDayMonth(etapa.fim),
    tooltip: tipCardToText(card),
    card,
    past: isPast(etapa, today),
  };
}

/**
 * Id da alocação do membro: o real, quando o bff informa; senão, um por alocação (o mesmo
 * profissional pode ter dois papéis na obra). É também a key da linha.
 */
function alocacaoDoMembro(
  obraId: string,
  profissional: { id: string; alocacaoId?: string; inicio: string },
): string {
  return profissional.alocacaoId ?? `${obraId}-${profissional.id}-${profissional.inicio}`;
}

/**
 * Visão por Obras: cada obra com a equipe alocada, uma linha por profissional. O selo de
 * conflitos da linha conta só os conflitos em aberto da própria alocação; os ignorados
 * (RN07) — informados pelo bff ou nesta sessão (`ignoradosLocais`) — ficam de fora.
 */
export function buildObraGroups(
  data: CargaTrabalhoObrasData,
  timeline: Timeline,
  indicators: GanttIndicators,
  today: IsoDate,
  ignoradosLocais: CargaTrabalhoConflitoIgnorado[] = [],
): ObraGroup[] {
  // Conflitos são do profissional: consideram suas alocações em todas as obras.
  const alocacoesPorProfissional = new Map<string, CargaTrabalhoAlocacao[]>();
  const ignorados = [
    ...data.obras.flatMap((obra) => obra.conflitosIgnorados ?? []),
    ...ignoradosLocais,
  ];
  for (const obra of data.obras) {
    for (const profissional of obra.profissionais) {
      const lista = alocacoesPorProfissional.get(profissional.id) ?? [];
      lista.push({
        id: alocacaoDoMembro(obra.id, profissional),
        papelObraId: profissional.papelObraId,
        inicio: profissional.inicio,
        fim: profissional.fim,
        obra,
      });
      alocacoesPorProfissional.set(profissional.id, lista);
    }
  }

  return data.obras.map((obra) => {
    const line = buildLine(obra, timeline, indicators, today);

    const members = obra.profissionais.map((profissional): ObraRowContent => {
      const key = alocacaoDoMembro(obra.id, profissional);
      const alocacao = { inicio: profissional.inicio, fim: profissional.fim };
      const item = buildObraItem(
        { key, obra, alocacao, recurso: recursoLabel(profissional.nome, profissional.time) },
        timeline,
        indicators,
        today,
      );
      const daAlocacao = calcularConflitos(
        alocacoesPorProfissional.get(profissional.id) ?? [],
      ).filter((conflito) => conflito.a.id === key || conflito.b.id === key);
      const conflitos = separarConflitos(daAlocacao, ignorados).abertos;
      const { tooltip } = conflictBands(conflitos, timeline, key);

      return {
        key,
        marcaKey: chaveMarcaMembro(obra.id, profissional),
        person: {
          name: profissional.nome,
          team: profissional.time,
          conflicts: conflitos.length,
          conflictTooltip: tooltip,
          conflictGroups: conflitos.map(toConflictGroup),
        },
        summary: null,
        line,
        pill: item
          ? {
              left: item.left,
              width: item.width,
              segments: item.segments,
              label: monthsLabel(alocacao),
              tooltip: item.tooltip,
              ...(item.card ? { card: item.card } : {}),
              past: item.past,
              cancelada: item.cancelada,
            }
          : null,
      };
    });

    const total = obra.profissionais.length;
    const summaryLabel =
      total === 0 ? NO_TEAM_LABEL : `${total} ${total === 1 ? "Profissional" : "Profissionais"}`;

    return {
      key: obra.id,
      obraId: obra.id,
      obraInicio: obra.etapas.obra?.inicio ?? null,
      obraFim: obra.etapas.obra?.fim ?? null,
      semEquipe: total === 0,
      codigo: obra.codigo,
      tipos: obra.tipos ?? [],
      nome: obra.nome,
      rotulo: identificacaoDaObra(obra),
      embarcacao: obra.embarcacao?.nome ?? null,
      embarcacaoSigla: obra.embarcacao?.acronimo ?? null,
      summary: {
        key: `${obra.id}-resumo`,
        marcaKey: `${obra.id}-resumo`,
        person: null,
        summary: summaryLabel,
        line,
        pill: null,
      },
      members,
    };
  });
}
